import { useContext, useMemo, useRef, type ReactNode } from 'react'

import type { SlotColumn } from '@/shared/registry'
import { cn } from '@/shared/ui'
import { insetFocusRing } from '@/shared/ui/cn'
import type { FocusRef } from '@/shared/ui/use-list-focus'
import { useNeedsStacking } from '@/shared/ui/use-needs-stacking'

import { NarrowPanelContext } from './narrow-panel'
import type { SortState } from './search-state'
import { SortableHeader } from './SortableHeader'

/** A column as the table draws it: its header, and each row's cell. */
export interface TableColumn<T> {
  readonly id: string
  readonly header: string
  readonly cell: (row: T, rowId: string) => ReactNode
  /** The API sort field its header sorts by, in a table given `onSort`; none when it does not sort. */
  readonly sortField?: string | undefined
  /** On a narrow screen, puts the column's name above the cell rather than beside it, so a form field gets the row's width. */
  readonly isNameAbove?: boolean | undefined
}

const noSorts: readonly SortState[] = []

// The mockups' gray header band with rounded top corners; its bottom rule is an inset shadow, so it
// travels with the header row while that sticks.
const headerCell =
  'whitespace-nowrap bg-background-contrast-sm px-4 py-3 shadow-[inset_0_-1px_0_var(--color-divider)] first:rounded-tl last:rounded-tr'

/**
 * The results table, styled as the mockups' Find pages draw theirs: no outer box, a gray header
 * band, and a rule under every row. From `lg` its header row sticks under the page header. On a
 * narrow screen, or wherever its room is narrower than its columns need, each row stacks into a
 * block, its cells one under another, each led by its column's name, under a row of the headers
 * that sort. `stacked:` is that narrow screen, narrow panel or narrow room, set in `index.css`.
 */
export function ResultsTable<T>(props: {
  /** The table's accessible name, such as the page's title. */
  readonly caption: string
  readonly columns: readonly TableColumn<T>[]
  /** Columns other features contribute, after the screen's own. */
  readonly slotColumns: readonly SlotColumn[]
  readonly rows: readonly T[]
  readonly rowId: (row: T) => string
  /** The order the viewer chose, highest priority first, which its columns' headers show; none for the screen's own. */
  readonly sorts?: readonly SortState[]
  /** Called with a header's sort field when it is pressed; without it no header sorts. */
  readonly onSort?: (field: string) => void
  /** Each sorted header shows its place in the order, on a screen that sorts by several columns at once. */
  readonly showsSortPlaces?: boolean
  /** A ref for each row, by its id, for a row that takes focus, such as the first one Load more adds. */
  readonly rowRef?: (id: string) => FocusRef
  /**
   * Also stacks while its own room is narrower than 56rem, as a zoomed page narrows it long before
   * the screen is. Always so in a details page (`NarrowPanelContext`); a page of its own whose
   * table has many columns opts in.
   */
  readonly isStackedInNarrowPanel?: boolean
}) {
  const { columns, slotColumns, rows, rowId, onSort, rowRef } = props
  const isInPanel = useContext(NarrowPanelContext)
  const hasSortHeaders = onSort !== undefined && columns.some((column) => column.sortField !== undefined)

  const isInNarrowPanel = isInPanel || props.isStackedInNarrowPanel === true
  const sectionRef = useRef<HTMLElement>(null)
  // A column shown or hidden, new rows or a new sort header change the width it needs as a grid.
  const content = useMemo(() => [columns, slotColumns, rows, props.sorts], [columns, slotColumns, rows, props.sorts])
  const needsStacking = useNeedsStacking(sectionRef, content)
  const fits = needsStacking === false

  const table = (
    // A table wider than its room stacks; until measured it scrolls sideways, which the keyboard
    // does once it has focus. One that fits lets its header row stick under the page header, which
    // a scrolling box would hold back.
    <section
      ref={sectionRef}
      aria-label={props.caption}
      data-stacked={needsStacking === true ? '' : undefined}
      // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scroll region must take focus
      tabIndex={0}
      className={cn('overflow-x-auto stacked:overflow-x-visible', fits && !isInNarrowPanel && 'lg:overflow-visible', insetFocusRing)}
    >
      <table className="w-full min-w-[640px] border-collapse text-sm stacked:block stacked:min-w-0">
        <caption className="sr-only">{props.caption}</caption>
        {/* On a narrow screen only the headers that sort show, as a row above the stacked records. */}
        <thead className={cn(hasSortHeaders ? 'stacked:block' : 'stacked:sr-only', fits && !isInNarrowPanel && 'lg:sticky lg:top-[var(--shell-header-height,0px)] lg:z-10')}>
          <tr className="text-left text-sm font-medium text-text-primary stacked:flex stacked:flex-wrap stacked:gap-x-4 stacked:gap-y-2 stacked:pb-3">
            {columns.map((column) =>
              column.sortField !== undefined && onSort !== undefined ? (
                <SortableHeader
                  key={column.id}
                  label={column.header}
                  field={column.sortField}
                  sorts={props.sorts ?? noSorts}
                  onSort={onSort}
                  showsPlace={props.showsSortPlaces === true}
                  className={cn(headerCell, 'stacked:bg-transparent stacked:p-0 stacked:shadow-none')}
                />
              ) : (
                <th key={column.id} scope="col" className={cn(headerCell, 'stacked:sr-only')}>
                  {column.header}
                </th>
              ),
            )}
            {slotColumns.map((column) => (
              <th key={column.id} scope="col" className={cn(headerCell, 'stacked:sr-only')}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="stacked:flex stacked:flex-col stacked:gap-3">
          {rows.map((row) => {
            const id = rowId(row)
            return (
              <tr
                key={id}
                ref={rowRef?.(id)}
                className={cn('border-b border-divider last:border-0 stacked:block stacked:rounded stacked:border stacked:py-2 stacked:last:border', insetFocusRing)}
              >
                {columns.map((column) => (
                  <Cell key={column.id} header={column.header} isNameAbove={column.isNameAbove === true}>
                    {column.cell(row, id)}
                  </Cell>
                ))}
                {slotColumns.map((column) => (
                  <Cell key={column.id} header={column.header}>
                    {column.renderCell(id)}
                  </Cell>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </section>
  )

  // The container `stacked:` measures, besides the screen.
  return isInPanel || props.isStackedInNarrowPanel === true ? <div className="@container/results-table w-full">{table}</div> : table
}

function Cell(props: { readonly header: string; readonly isNameAbove?: boolean; readonly children: ReactNode }) {
  return (
    <td className={cn('px-4 py-3 text-text-primary stacked:flex stacked:py-1.5', props.isNameAbove === true ? 'stacked:flex-col stacked:gap-1' : 'stacked:gap-3')}>
      {/* The column's name, for the stacked rows; the table header names it otherwise. */}
      <span aria-hidden="true" className="hidden w-32 shrink-0 font-medium text-text-secondary stacked:block">
        {props.header}
      </span>
      <span className="min-w-0 break-words">{props.children}</span>
    </td>
  )
}
